<template>
  <div class="card-body py-3" v-if="!isLoaded || !isEmpty(leaderships)">
    <h3 class="mb-3 t-md t-400 d-flex t-primary justify-content-between">
      Leadership
      <a v-if="leadershipUrl" :href="leadershipUrl.url" _target="blank"><i class="t-lg fa fa-user is-hover--blue t-secondary"></i></a>
    </h3>
    <template v-if="isLoaded">
      <div v-for="leader in leaderships" :key="`${leader.full_name}-${leader.role}`" class="mb-3">
        <p class="mb-0 t-600 t-regular">{{ getLeadershipTitle(leader) }}</p>
        <div class="d-flex justify-content-between align-items-center">
          <span><i class="t-lr fa fa-user t-secondary t-sm mr-3"></i>
            <router-link
              v-if="leader.researcher && !isEmpty(leader.researcher.slug_values)"
              :to="{
                name: 'researcher.show',
                params: { id: leader.researcher.slug_values[0]},
                query: { fullName: stripTags(leader.full_name) }
              }">{{leader.full_name}}</router-link>
            <span v-else>{{leader.full_name}}</span>
          </span>
          <div>
            <dropdown placement="bottom-end" noPadding style="display: inline-block;">
              <template #button>
                <a><i class="fa fa-envelope t-secondary t-sm is-hover--blue"></i></a>
              </template>
              <div class="dropdown-menu-custom box-shadow p-3">
                <div class="t-sm">Find a verified email?<br />
                  <span class="t-xs">This operation takes a few minutes, and consumes 1 contact credit if successful.</span></div>
                <button
                  class="btn btn-primary no-border ml-0 mt-2"
                  type="button"
                  @click="findVerifiedEmail(stripTags(leader.full_name), leader.linked_in)">Yes
                </button>
                <button
                  class="btn bg-transparent no-border ml-3 mt-2"
                  type="button"
                  v-close-popover>No
                </button>
              </div>
            </dropdown>
          <a class="ml-2" v-if="leader.linked_in" :href="leader.linked_in" target="_blank"><i class="fa fa-linkedin t-secondary t-sm is-hover--blue"></i></a>
          </div>
        </div>
        <long-text v-if="leader.about" :text="leader.about" :truncateAt="150" :isRichText="true" class="mt-1 t-sm t-400" />
      </div>
    </template>
    <!-- render spinner if not loaded -->
    <spinner v-if="!isLoaded" :containerClass="`width--50px min-height--150px`" />
  </div>
</template>

<script>
import Spinner from '@/components/shared/Spinner'
import LongText from '@/components/shared/LongText'
import organisationMixins from '@/mixins/organisation'
import Dropdown from '@/components/shared/form/Dropdown'
import EmailService from '@/services/email'

export default {
  name: 'organisationLeaderships',
  mixins: [organisationMixins],
  components: {
    Spinner,
    LongText,
    Dropdown
  },
  props: {
    isLoaded: {
      type: Boolean,
      required: true
    },
    organisation: {
      type: Object,
      required: true
    }
  },
  computed: {
    leadershipUrl () {
      return !this.isEmpty(this.organisation.urls) && this.organisation.urls.find(organisation => {
        return organisation.note === 'LEADERSHIP'
      })
    },
    leaderships () {
      if (!this.organisation.highlights || !this.organisation.highlights.leaderships) return null
      // PROPOSED ORDERING: https://www.notion.so/academiclabscom/Organisations-Leadership-ordering-240eb851d670478cbfc9851f25dedc8b#096340af987349889330c016aa6183f5
      const updatedLeaderships = []
      const firstOrder = ['chief executive officer']
      const secondOrder = ['president']
      const thirdOrder = ['chief scientific officer', 'chief science officer', 'chief research officer', 'chief technology officer']
      const fourthOrder = ['senior vice president, research and development', 'vice president of research and development']
      const fifthOrder = ['chief medical officer']
      const sixthOrder = ['chief business development officer', 'chief business officer']
      const seventhOrder = ['director', 'chairman']

      const generateSortValue = (leader) => {
        const role = leader.role.trim().toLowerCase()
        if (firstOrder.includes(role)) return 0
        else if (secondOrder.includes(role)) return 1
        else if (thirdOrder.includes(role)) return 2
        else if (fourthOrder.includes(role)) return 3
        else if (fifthOrder.includes(role)) return 4
        else if (sixthOrder.includes(role)) return 5
        else if (role.includes('chief')) return 6
        else if (seventhOrder.includes(role)) return 7
        else if (role.includes('advisor')) return 8
        else if ((role.includes('vice president') || role.includes('vp'))) return 9
        else if (role.includes('director')) return 10
        else if (role.includes('head of')) return 11
        else return 999
      }

      this.organisation.highlights.leaderships.forEach(leader => {
        if (!leader.role) return
        updatedLeaderships.push({ ...leader, sortValue: generateSortValue(leader) })
      })
      updatedLeaderships.sort((a, b) => a.sortValue - b.sortValue)
      return updatedLeaderships
    },
    website () {
      // (copy-pasted from OrganisationInformation)
      if (this.isEmpty(this.organisation.urls)) return null
      return this.organisation.urls.find(organisation => {
        return organisation.note === 'PD'
      })
    }
  },
  methods: {
    findVerifiedEmail (fullName, linkedIn) {
      EmailService.findEmail({
        fullName: fullName,
        organisationName: this.organisation.name,
        organisationUrl: this.website,
        linkedIn: linkedIn,
        organisationSlugs: this.organisation.slug_values
      }).then(result => {
        this.$flash({ message: result.message }, 'wait')
      }).catch(err => {
        this.$flash({ message: this.renderError(err) }, 'warning')
      })
    }
  }
}
</script>
